import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState, useCallback, useMemo, useEffect } from 'react';
import Creatable from 'react-select/creatable';
import makeAnimated from 'react-select/animated';
import { SearchIcon } from '@heroicons/react/outline';
import toast from 'react-hot-toast';

const animatedComponents = makeAnimated();

// Helper function to fetch customer ID for a single email
const fetchCustomerId = async (projectId, email, getAppKey, fetchEngageData) => {
	try {
		const response = await fetchEngageData(
			projectId,
			'/customer-id',
			{ email, app_key: getAppKey() },
			null,
			'GET'
		);
		// Handle different response structures
		let customerId = null;
		if (response?.data?.customer_id) {
			customerId = response.data.customer_id;
		} else if (response?.customer_id) {
			customerId = response.customer_id;
		}
		
		return customerId;
	} catch (err) {
		// Check if it's a 204 status (no user found)
		if (err?.response?.status === 204) {
			console.warn(`No user found for email: ${email}`);
			return null; // Return null to indicate user not found
		}
		console.error(`Failed to get customer ID for ${email}:`, err);
		throw err; // Re-throw other errors
	}
};

const TestNotification = ({ isOpen, setIsOpen, onSendTest, projectId }) => {
	const [selectedEmails, setSelectedEmails] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [invalidEmails, setInvalidEmails] = useState([]);
	const [emailCustomerIds, setEmailCustomerIds] = useState({}); // Store customer IDs for each email
	const [loadingCustomerIds, setLoadingCustomerIds] = useState({}); // Track loading state for each email

	// Helper function to validate email format
	const isValidEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	// Get array of email values from selected options
	const emailValues = useMemo(() => 
		selectedEmails.map(item => item.value),
	[selectedEmails]);

	// Validate emails whenever selectedEmails changes
	useEffect(() => {
		const invalid = emailValues.filter(email => !isValidEmail(email));
		setInvalidEmails(invalid);
	}, [emailValues]);

	// Fetch customer IDs for new emails
	useEffect(() => {
		const fetchCustomerIdsForNewEmails = async () => {
			// Import the required functions dynamically to avoid circular dependency
			const { getProjectData } = await import('@appmaker/core/store/project');
			const { default: axios } = await import('axios');
			const { V3_BASE_URL } = await import('@appmaker/core/api/core');
			
			// Helper function to get app key from project data
			const getAppKey = () => {
				const { projectData } = getProjectData();
				return projectData?.app_key || projectData?.meta?.app_key || 'placeholder-app-key';
			};

			// Helper function to fetch engage data
			const fetchEngageData = async (projectId, endpoint, params, signal, method = 'GET', data = null) => {
				if (!projectId) {
					return Promise.reject(new Error("Project ID is required"));
				}
				
				const url = `${V3_BASE_URL}/projects/${projectId}/p-engage${endpoint}`;
				
				const config = {
					method,
					url,
					withCredentials: true,
					signal,
					timeout: 30000,
				};

				if (method === 'GET') {
					config.params = params;
				} else {
					config.data = data;
					if (params && Object.keys(params).length > 0) {
						config.params = params;
					}
				}

				const response = await axios(config);
				return response.data;
			};

			// Find emails that don't have customer IDs yet and are valid
			// Also check if the email has been processed (either has a customer ID or has been checked and found to be null)
			const newEmails = emailValues.filter(email => 
				isValidEmail(email) && 
				emailCustomerIds[email] === undefined && 
				!loadingCustomerIds[email]
			);

			if (newEmails.length === 0) return;

			// Set loading state for new emails
			setLoadingCustomerIds(prev => ({
				...prev,
				...Object.fromEntries(newEmails.map(email => [email, true]))
			}));

			// Fetch customer IDs for new emails
			for (const email of newEmails) {
				try {
					const customerId = await fetchCustomerId(projectId, email, getAppKey, fetchEngageData);
					
					setEmailCustomerIds(prev => ({
						...prev,
						[email]: customerId // This will be null if user not found, or the actual customer ID
					}));
				} catch (error) {
					console.error(`Error fetching customer ID for ${email}:`, error);
					// Don't set emailCustomerIds for actual errors, let it retry
				} finally {
					setLoadingCustomerIds(prev => ({
						...prev,
						[email]: false
					}));
				}
			}
		};

		if (projectId && emailValues.length > 0) {
			fetchCustomerIdsForNewEmails();
		}
	}, [emailValues, projectId, emailCustomerIds, loadingCustomerIds]);

	// Get all valid customer IDs
	const validCustomerIds = useMemo(() => {
		return emailValues
			.filter(email => isValidEmail(email) && emailCustomerIds[email])
			.map(email => emailCustomerIds[email]);
	}, [emailValues, emailCustomerIds]);

	// Get emails that were not found
	const notFoundEmails = useMemo(() => {
		return emailValues.filter(email => 
			isValidEmail(email) && 
			emailCustomerIds[email] === null
		);
	}, [emailValues, emailCustomerIds]);

	// Get emails that are still loading
	const loadingEmails = useMemo(() => {
		return emailValues.filter(email => 
			isValidEmail(email) && 
			loadingCustomerIds[email]
		);
	}, [emailValues, loadingCustomerIds]);

	// Check if all emails are valid and have customer IDs
	const allEmailsValid = useMemo(() => {
		return selectedEmails.length > 0 && 
			   invalidEmails.length === 0 && 
			   notFoundEmails.length === 0 &&
			   loadingEmails.length === 0 &&
			   validCustomerIds.length === selectedEmails.length;
	}, [selectedEmails.length, invalidEmails.length, notFoundEmails.length, loadingEmails.length, validCustomerIds.length]);

	const handleSendTest = useCallback(async () => {
		if (!selectedEmails || selectedEmails.length === 0) {
			toast.error('Please add at least one email address');
			return;
		}

		// Validate all emails before proceeding
		if (invalidEmails.length > 0) {
			toast.error(`Invalid email format: ${invalidEmails.join(', ')}`);
			return;
		}

		// Check if any emails are still loading
		if (loadingEmails.length > 0) {
			toast.error('Some emails are still being verified. Please wait for verification to complete.');
			return;
		}

		// Check if any emails were not found
		if (notFoundEmails.length > 0) {
			toast.error(`Cannot send notification. ${notFoundEmails.length} email${notFoundEmails.length !== 1 ? 's' : ''} not found in your app. Please remove them first.`);
			return;
		}

		// Check if all emails have customer IDs
		if (validCustomerIds.length !== selectedEmails.length) {
			toast.error('Some emails are still being verified. Please wait or remove unverified emails.');
			return;
		}

		setIsLoading(true);
		
		try {
			console.log('Sending test notification with customer IDs:', validCustomerIds);
			// Pass the customer IDs to the parent component
			await onSendTest(validCustomerIds);
			
			// Close modal and reset on success
			setIsOpen(false);
			setSelectedEmails([]);
			setEmailCustomerIds({});
			setLoadingCustomerIds({});
			toast.success(`Successfully sent test notification to ${validCustomerIds.length} recipient(s)`);
			
		} catch (error) {
			console.error('Error sending test notification:', error);
			toast.error('Failed to send test notification');
		} finally {
			setIsLoading(false);
		}
	}, [selectedEmails, onSendTest, setIsOpen, validCustomerIds, invalidEmails.length, loadingEmails.length, notFoundEmails.length]);

	const closeModal = useCallback(() => {
		setIsOpen(false);
		setSelectedEmails([]);
		setEmailCustomerIds({});
		setLoadingCustomerIds({});
	}, [setIsOpen]);

	const handleEmailChange = useCallback((newValue) => {
		setSelectedEmails(newValue || []);
	}, []);

	// Custom create option to validate email format
	const handleCreateOption = useCallback((inputValue) => {
		if (!isValidEmail(inputValue)) {
			toast.error('Please enter a valid email address');
			return;
		}
		
		const newOption = {
			label: inputValue,
			value: inputValue
		};
		
		setSelectedEmails(prev => [...prev, newOption]);
	}, []);

	return (
		<>
			<Transition appear show={isOpen} as={Fragment}>
				<Dialog as="div" className="relative z-10" onClose={closeModal}>
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div className="fixed inset-0 bg-black bg-opacity-50" />
					</Transition.Child>

					<div className="fixed inset-0 overflow-y-auto">
						<div className="flex min-h-full items-center justify-center p-4 text-center">
							<Transition.Child
								as={Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0 scale-95"
								enterTo="opacity-100 scale-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100 scale-100"
								leaveTo="opacity-0 scale-95"
							>
								<Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
									<Dialog.Title
										as="h3"
										className="text-lg font-medium leading-6 text-gray-900"
									>
										Test Push Notification
									</Dialog.Title>
									<div className="mt-4">
										<p className="text-sm text-gray-500 mb-3">
											Send a test push notification to the following email addresses
										</p>
										<div className="flex flex-row items-center border border-gray-200 rounded-md group focus-within:border-blue-500">
											<SearchIcon className="w-4 h-4 text-gray-500 ml-2" />
											<div className="flex-1">
												<Creatable
													options={[]}
													placeholder="Enter email addresses..."
													components={animatedComponents}
													isMulti
													value={selectedEmails}
													onChange={handleEmailChange}
													onCreateOption={handleCreateOption}
													formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
													styles={{
														control: (base) => ({
															...base,
															border: 'none',
															borderRadius: '6px',
															fontSize: '13px',
															color: '#374151',
															outline: 'none',
															ring: 'none',
															boxShadow: 'none',
														}),
														multiValue: (base) => ({
															...base,
															backgroundColor: '#e5e7eb',
														}),
														multiValueLabel: (base) => ({
															...base,
															color: '#374151',
														}),
													}}
												/>
											</div>
										</div>
										{selectedEmails.length > 0 && (
											<div className="mt-2">
												<p className="text-xs text-gray-500">
													{selectedEmails.length} email{selectedEmails.length !== 1 ? 's' : ''} selected
												</p>
												{invalidEmails.length > 0 && (
													<div className="mt-1">
														<p className="text-xs text-red-500 font-medium">
															⚠️ Invalid email format detected
														</p>
														<div className="mt-1">
															{invalidEmails.map((email, index) => (
																<p key={index} className="text-xs text-red-400">
																	• {email}
																</p>
															))}
														</div>
														<p className="text-xs text-gray-500 mt-1">
															Please remove invalid emails or correct their format to continue
														</p>
													</div>
												)}
												
												{/* Customer ID Status */}
												<div className="mt-2">
													<p className="text-xs text-gray-500 font-medium mb-1">
														Customer ID Status:
													</p>
													{emailValues.map((email) => {
														if (!isValidEmail(email)) return null;
														
														const isLoading = loadingCustomerIds[email];
														const customerId = emailCustomerIds[email];
														
														return (
															<div key={email} className="flex items-center justify-between text-xs mb-1">
																<span className="text-gray-600 truncate flex-1">{email}</span>
																<div className="flex items-center ml-2">
																	{isLoading ? (
																		<span className="text-blue-500">⏳ Verifying...</span>
																	) : customerId ? (
																		<span className="text-green-500">✅ {customerId}</span>
																	) : customerId === null ? (
																		<span className="text-red-500">❌ Not found</span>
																	) : (
																		<span className="text-gray-400">⏸️ Pending</span>
																	)}
																</div>
															</div>
														);
													})}
												</div>

												{/* Not Found Emails Warning */}
												{notFoundEmails.length > 0 && (
													<div className="mt-2">
														<p className="text-xs text-red-500 font-medium">
															⚠️ {notFoundEmails.length} email{notFoundEmails.length !== 1 ? 's' : ''} not found in your app
														</p>
														<div className="mt-1">
															{notFoundEmails.map((email, index) => (
																<p key={index} className="text-xs text-red-400">
																	• {email}
																</p>
															))}
														</div>
														<p className="text-xs text-gray-500 mt-1">
															Please remove these emails to enable sending notifications.
														</p>
													</div>
												)}
											</div>
										)}
									</div>

									<div className="mt-6 flex flex-row gap-2 items-center justify-end">
										<button
											type="button"
											className="inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-blue-900 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50"
											onClick={closeModal}
											disabled={isLoading}
										>
											Cancel
										</button>
										<button
											type="button"
											className="inline-flex justify-center rounded-md border border-transparent bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
											onClick={handleSendTest}
											disabled={isLoading || !allEmailsValid}
										>
											{isLoading ? 'Processing...' : 'Send Test'}
										</button>
									</div>
								</Dialog.Panel>
							</Transition.Child>
						</div>
					</div>
				</Dialog>
			</Transition>
		</>
	);
};

export default TestNotification;
